import { Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { NextPage } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import useSWR from 'swr'
import { useAccount } from 'wagmi'
import { getMiningRewards, getMyMiningData, getRoundTradeMining } from '~/apis'
import IconGrayChevronRight from '~/assets/images/gray-chevron-right.svg'
import IconTradeMining from '~/assets/images/trade-mining-light.svg'
import DataContainer from '~/components/DataContainer'
import FoldText from '~/components/FoldText'
import Footer from '~/components/Footer'
import FullPageModal from '~/components/FullPageModal'
import Header from '~/components/Header'
import { TRADE_STATUS } from '~/components/TradeDetail/types'
import SimpleStatus from '~/components/TradeDetail/components/SimpleStatus'
import { useBreakpoint, useGA } from '~/hooks'
import { useMerkleRedeemContract } from '~/hooks/useContract'
import { LOCALES } from '~/locales'
import { useModalOpened, useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { formatNumber, formatPercentage, toBigNumber } from '~/utils/numbers'
import { goV5Tokenlon } from '~/utils/userAgent'
import { MiningData, PhaseRecord, actionsheet } from './components'
import {
  StyledLonPageHelper,
  StyledTradeWrapper,
  StyledWarningWrap,
  StyledTitleIcon,
  StyledFixedBtnWrap,
  StyledGoTradeBtn,
} from './styled'
import { handleTxError } from '~/utils/handleTxError'
import { message } from '~/components/Message'
import NotificationBar from '~/components/NotificationBar'
import Space from '~/components/Space'
import { useLocalStorage } from '~/hooks/useLocalStorage'

const TradeMining: NextPage = () => {
  const theme = useTheme()
  const { isMobile } = useBreakpoint()
  const { i18n, t } = useTranslation()
  const { address: account } = useAccount()
  const contract = useMerkleRedeemContract()

  const notificationLink =
    'https://snapshot.org/#/s:tokenlon.eth/proposal/0xf464889233c044b056cc55f1e634664b815f3f7ea1c54212ab820f52eee0e0f1'

  const [visitedNotification, setVisitedNotification] = useLocalStorage(
    'trade-mining-notification-20260122-visited',
    false,
  )

  const handleNotificationLinkClick = () => {
    window.open(notificationLink, '_blank', 'noopener,noreferrer')
  }

  const { trackElClickCurring, trackElClick, trackClick } = useGA()
  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    modalState: { from: 'trade_mining' },
  })
  const isOpen = useModalOpened(ApplicationModal.CLAIMING)
  const toggleModal = useModalToggle(ApplicationModal.CLAIMING)

  const showTradeMiningSections = false

  const isZh = i18n.resolvedLanguage === LOCALES.ZH
  const ruleUrl = isZh
    ? 'https://support.tokenlon.im/hc/zh-cn/articles/360058990571'
    : 'https://support.tokenlon.im/hc/en-us/articles/360058990571'

  const [status, setStatus] = useState<TRADE_STATUS>()
  const [txHash, setTxHash] = useState<string>()

  const { data: miningRecords } = useSWR('getMiningRewards', getMiningRewards)
  const { data: myData } = useSWR(['getMyMiningData', account], () =>
    getMyMiningData(account),
  )
  const { data: roundTradeMinings } = useSWR(
    ['getRoundTradeMining', account],
    () => getRoundTradeMining(account),
  )
  const isClaimable = roundTradeMinings
    ?.filter((t) => t.phase !== 0)
    ?.some((t) => t.claim && t.claim.proof)
  const claimAmount = roundTradeMinings
    ?.filter((t) => t.phase !== 0)
    ?.reduce((acc, item) => {
      if (item.claim && item.claim.balance) {
        acc = acc.plus(toBigNumber(item.claim.balance))
      }
      return acc
    }, toBigNumber(0))
  const isClaiming =
    status === TRADE_STATUS.UN_BROADCAST || status === TRADE_STATUS.PENDING

  const handleClaimRecords = async () => {
    const claims = roundTradeMinings
      ?.filter((t) => t.claim && t.claim.proof && t.phase !== 0)
      .map((t) => ({
        period: t.claim.period,
        balance: t.claim.balance,
        proof: t.claim.proof,
      }))
    toggleModal()
    setStatus(TRADE_STATUS.UN_BROADCAST)
    try {
      setStatus(TRADE_STATUS.PENDING)
      const result = await contract.claimPeriods(account, claims)
      if (result?.hash) {
        setTxHash(result?.hash)
      }
      result.wait().then((receipt) => {
        if (receipt && receipt.status) {
          trackClick('webtl_trademining_draw_confirm', {
            is_success: 1,
            amount: claimAmount.toString(),
          })
          setStatus(TRADE_STATUS.SUCCESS)
          setTxHash(receipt.transactionHash)
        }
      })
    } catch (error) {
      const errorMessage =
        typeof error === 'string'
          ? error
          : error?.reason || error?.message || error?.code
      trackClick('webtl_trademining_draw_confirm', {
        is_success: 0,
        amount: claimAmount.toString(),
        message: errorMessage,
      })
      setStatus(TRADE_STATUS.FAILED)
      if (errorMessage?.toLowerCase()?.includes('user rejected')) {
        message.error(t('user_rejected_message'))
      } else {
        handleTxError(error)
      }
    }
  }

  const onClose = () => {
    setStatus(undefined)
    setTxHash(undefined)
    if (isOpen) toggleModal()
  }

  return (
    <StyledLonPageHelper>
      <Head>
        <title>{t('meta_lon_trade_title')}</title>
        <meta name="description" content={t('meta_lon_trade_description')} />
        <meta name="keywords" content={t('meta_lon_keywords')} />
      </Head>
      <Header />
      <StyledTradeWrapper key="trade">
        <div className="normal-wrap">
          {!visitedNotification && (
            <>
              <NotificationBar
                text={t('trade_mining_notification')}
                handleClose={() => setVisitedNotification(true)}
                handleLinkClick={handleNotificationLinkClick}
              />
              <Space height={40} />
            </>
          )}
          <section className="intro">
            <div className="intro-header">
              <StyledTitleIcon src={IconTradeMining} />
              <dt className="title">{t('trade_mining')}</dt>
            </div>
            <dd className="intro-desc">
              <FoldText texts={[t('trade_mining_desc')]} />
              <a className="purple-link" href={ruleUrl} target="_blank">
                {t('more_details')} {'>'}
              </a>
            </dd>
          </section>

          {showTradeMiningSections && (
            <>
              <MiningData />
              <section className="my-trade">
                <div className="title">
                  <p>{t('my_tokenlon_data')}</p>
                  {!isMobile && (
                    <div className="group">
                      <StyledGoTradeBtn onClick={() => goV5Tokenlon(account)}>
                        {t('go_trade')}
                      </StyledGoTradeBtn>
                    </div>
                  )}
                </div>
                {account ? (
                  <div className="cards">
                    <DataContainer
                      dataTitle={t('current_total_trade')}
                      description={`$${formatNumber(myData?.myTradeVolume, 2)}`}
                      title=""
                    />
                    <DataContainer
                      dataTitle={t('current_trade_power')}
                      description={`${formatNumber(myData?.myTradePower, 2)}`}
                      contentStyle={{ color: '#4DD8D0' }}
                      className="data-container__nested"
                      title={null}
                      pie={
                        <span>
                          {`${t('current_total_power')}`}&nbsp;
                          <span className="purple">
                            {`${formatNumber(myData?.tradePower, 2)}`}
                          </span>
                        </span>
                      }
                    />
                    <DataContainer
                      dataTitle={t('current_power_share')}
                      description={
                        myData
                          ? formatPercentage(
                              myData?.myTradePower / myData?.tradePower,
                              6,
                            )
                          : '~'
                      }
                      title={t('current_power_share_info')}
                    />
                  </div>
                ) : (
                  <div className="placeholder">
                    <span
                      className="clickable"
                      data-trackid="webtl_walletconnect"
                      data-trackdata={JSON.stringify({ from: 'trade_mining' })}
                      onClick={(e) => trackElClickCurring(e)(toggleWalletModal)}
                    >
                      {t('connect')}
                    </span>
                    <span>{t('to_view_data')}</span>
                  </div>
                )}
              </section>

              <section className="my-invitation" id="my-invitations">
                <div className="title">
                  <p>{t('my_invitation')}</p>

                  {!isMobile && (
                    <div className="group">
                      <Link href="/lon/mining/trade/activate">
                        <button
                          className="secondary"
                          data-trackid="webtl_trademining_bind"
                          data-trackdata={JSON.stringify({
                            url: '/lon/mining/trade/activate',
                          })}
                          onClick={trackElClick}
                        >
                          {t('activate_btn')}
                        </button>
                      </Link>
                      <Link href="/lon/mining/trade/share">
                        <button
                          data-trackid="webtl_trademining_invite"
                          data-trackdata={JSON.stringify({
                            url: '/lon/mining/trade/share',
                          })}
                          onClick={trackElClick}
                        >
                          {t('invite_btn')}
                        </button>
                      </Link>
                    </div>
                  )}
                </div>
                <StyledWarningWrap>
                  <p>
                    <span className="bold">{t('my_invitation_tip_1')}</span>
                    {t('my_invitation_tip_2')}
                  </p>
                </StyledWarningWrap>
                {account ? (
                  <div className="cards">
                    <DataContainer
                      dataTitle={t('current_invitation_activated')}
                      description={
                        <a href="/lon/mining/trade/invitations">
                          <div
                            className="flex-row-between"
                            style={{ width: '100%' }}
                          >
                            <span>
                              {myData ? `${myData?.activateInvitations}` : '~'}
                            </span>
                            <span style={{ marginTop: -26 }}>
                              <img src={IconGrayChevronRight} alt="" />
                            </span>
                          </div>
                        </a>
                      }
                      title=""
                    />

                    <DataContainer
                      dataTitle={t('current_invitation_power')}
                      description={
                        myData
                          ? `${formatNumber(myData?.myReferralPower, 2)}`
                          : '~'
                      }
                      className="data-container__nested"
                      title={null}
                      contentStyle={{ color: theme.colors.chartPurple }}
                      pie={
                        <span>
                          {t('total_referral_power')}&nbsp;
                          <span className="purple">
                            {myData
                              ? `${formatNumber(myData?.referralPower, 2)}`
                              : '~'}
                          </span>
                        </span>
                      }
                    />

                    <DataContainer
                      dataTitle={t('current_power_share')}
                      description={
                        myData
                          ? formatPercentage(
                              myData?.myReferralPower / myData?.referralPower,
                              6,
                            )
                          : '~'
                      }
                      title={t('current_power_myshare_info')}
                    />
                  </div>
                ) : (
                  <div className="placeholder">
                    <span
                      className="clickable"
                      data-trackid="webtl_walletconnect"
                      data-trackdata={JSON.stringify({ from: 'trade_mining' })}
                      onClick={(e) => trackElClickCurring(e)(toggleWalletModal)}
                    >
                      {t('connect')}
                    </span>
                    <span>{t('to_view_data')}</span>
                  </div>
                )}
              </section>
            </>
          )}

          <section className="reward-record">
            <div className="title">
              <p style={{ color: theme.colors.textContent }}>
                {t('reward_record')}
              </p>
              {isClaimable && (
                <section style={{ whiteSpace: 'nowrap' }}>
                  <span className="to-claim">
                    {t('to_claim')} {formatNumber(claimAmount, 2, 18)} LON
                  </span>
                  <button
                    className="to-claim-btn"
                    data-trackid="webtl_trademining_draw"
                    onClick={(e) => trackElClickCurring(e)(handleClaimRecords)}
                  >
                    {isClaiming && (
                      <Spin
                        indicator={
                          <LoadingOutlined
                            style={{ fontSize: 12, color: '#fff' }}
                            spin
                          />
                        }
                        style={{ marginRight: 5 }}
                      />
                    )}
                    {isClaiming ? t('claiming') : t('claim')}
                  </button>
                </section>
              )}
            </div>
            {account ? (
              <PhaseRecord data={roundTradeMinings} records={miningRecords} />
            ) : (
              <div className="placeholder">
                <span>{t('no_records')}</span>
              </div>
            )}
          </section>
        </div>
        {showTradeMiningSections && isMobile && (
          <StyledFixedBtnWrap>
            <button
              className="secondary"
              onClick={() => {
                actionsheet.show([
                  {
                    label: t('lon_share_title'),
                    url: '/lon/mining/trade/share',
                  },
                  {
                    label: t('activate_btn'),
                    url: '/lon/mining/trade/activate',
                  },
                ])
              }}
            >
              {t('invite_btn')}
            </button>
            <StyledGoTradeBtn onClick={() => goV5Tokenlon(account)} width="48%">
              {t('go_trade')}
            </StyledGoTradeBtn>
          </StyledFixedBtnWrap>
        )}
        {isOpen && status && (
          <FullPageModal onClose={onClose} visible={isOpen}>
            <SimpleStatus
              status={status}
              type={ApplicationModal.CLAIMING}
              txHash={txHash}
            />
          </FullPageModal>
        )}
      </StyledTradeWrapper>
      <Footer />
    </StyledLonPageHelper>
  )
}

export default TradeMining
